<template>
  <div class="partner-container">
    <!-- Banner部分 -->
    <div 
      class="banner" 
      :class="[$i18n.locale]"
      :style="{ height: isMobile ? (isChinese ? '50vh' : '70vh') : '135vh' }"
    >
      <div class="banner-content">
        <h1 class="banner-title" ref="bannerTitle">{{ t("aboutkangma.partner") }}</h1>
      </div>

      <div class="breadcrumb" ref="breadcrumb">
        <span class="breadcrumb-item">   <router-link to="/about" @click.prevent>
             {{ t("header.aboutCompany") }}
       </router-link></span>
        <svg
          class="breadcrumb-separator"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="#B2B2B2"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="breadcrumb-item current">{{
          t("aboutkangma.partner")
        }}</span>
      </div>

   <div 
    class="partner-description" 
    ref="partnerDescription"
    :style="{ maxWidth: $i18n.locale === 'en' ? '900px' : '640px' }"
  >
    <p>{{ t("aboutkangma.partnerinfo") }}</p>
  </div>
    </div>
    
    <!-- 第一个内容区域 -->
    <div class="content-box" ref="contentBox1">
      <div class="main">
        <h3 ref="title1">{{ t("aboutkangma.partnertile") }}</h3>
        <p ref="text1">{{ t("aboutkangma.partnerinfo2") }}</p>
        <div class="logoimg" ref="logoImg1">
          <img src="@/assets/image/about/partner_2logo.png" alt="合作伙伴logo" />
        </div>
      </div>
    </div>
    

    <div class="content-box mapwrap" ref="contentBox2">
      <div class="main">
        <h3 ref="title2">{{ t("aboutkangma.partnertitle2") }}</h3>
        <p ref="text2">{{ t("aboutkangma.partnerinfo3") }}</p>
        <div class="logoimg" ref="logoImg2">
          <img src="@/assets/image/about/partner_3map.png" alt="全球合作伙伴地图" />
        </div>
      </div>
    </div>
    

    <div class="particles" ref="particles"></div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// 注册ScrollTrigger插件
gsap.registerPlugin(ScrollTrigger);

export default {
  name: "PartnerPage",
  setup() {
    const { t } = useI18n();
    return { t };
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 768;
    },
    isChinese() {
      return this.$i18n.locale === 'zh' || this.$i18n.locale === 'zh-CN';
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initAnimations();
      this.createParticles();
    });
  },
  methods: {
    initAnimations() {
      const bannerTitle = this.$refs.bannerTitle;
      if (bannerTitle) {
        const titleText = bannerTitle.innerText;
        bannerTitle.innerHTML = "";
        titleText.split("").forEach((char, i) => {
          const span = document.createElement("span");
          span.className = "char";
          span.textContent = char;
          span.style.display = "inline-block";
          span.style.opacity = "0";
          span.style.transform = "translateY(100px) rotate(20deg) scale(0.5)";
          bannerTitle.appendChild(span);

          gsap.to(span, {
            opacity: 1,
            y: 0,
            rotation: 0,
            scale: 1,
            duration: 1.2,
            delay: i * 0.08,
            ease: "elastic.out(1.2, 0.5)",
          });
        });
      }

      // 面包屑导航动画
      if (this.$refs.breadcrumb) {
        gsap.fromTo(
          this.$refs.breadcrumb,
          { 
            opacity: 0,
            x: -100,
            rotationY: 90 
          },
          {
            opacity: 1,
            x: 0,
            rotationY: 0,
            duration: 1.5,
            delay: 1.2,
            ease: "power3.out",
          }
        );
      }

      // 合作伙伴描述动画
      if (this.$refs.partnerDescription) {
        gsap.fromTo(
          this.$refs.partnerDescription,
          {
            opacity: 0,
            scale: 0.8,
            filter: "blur(10px)"
          },
          {
            opacity: 1,
            scale: 1,
            filter: "blur(0px)",
            duration: 1.8,
            delay: 1.8,
            ease: "power4.out"
          }
        );
      }

      // 第一个内容区域的动画
      if (this.$refs.contentBox1) {
        // 标题动画
        gsap.fromTo(
          this.$refs.title1,
          {
            opacity: 0,
            y: 80,
            scale: 0.7,
            rotationX: 45
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotationX: 0,
            duration: 1.2,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: this.$refs.contentBox1,
              start: "top 85%",
              end: "top 60%",
              toggleActions: "play none none reverse",
            }
          }
        );

        // 文字动画
        gsap.fromTo(
          this.$refs.text1,
          {
            opacity: 0,
            x: -100,
            filter: "blur(5px)"
          },
          {
            opacity: 1,
            x: 0,
            filter: "blur(0px)",
            duration: 1.5,
            delay: 0.3,
            scrollTrigger: {
              trigger: this.$refs.contentBox1,
              start: "top 80%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            }
          }
        );

        // Logo图片动画
        if (this.$refs.logoImg1) {
          const logo1 = this.$refs.logoImg1.querySelector('img');
          gsap.fromTo(
            logo1,
            {
              opacity: 0,
              scale: 0.3,
              rotation: -15,
              y: 100
            },
            {
              opacity: 1,
              scale: 1,
              rotation: 0,
              y: 0,
              duration: 1.8,
              ease: "elastic.out(1.2, 0.5)",
              scrollTrigger: {
                trigger: this.$refs.logoImg1,
                start: "top 90%",
                end: "top 60%",
                toggleActions: "play none none reverse",
              }
            }
          );
        }
      }

      // 第二个内容区域的动画
      if (this.$refs.contentBox2) {
        // 标题动画（从右侧进入）
        gsap.fromTo(
          this.$refs.title2,
          {
            opacity: 0,
            x: 50,
            rotationY: 20,
            color: "#4fc3f7"
          },
          {
            opacity: 1,
            x: 0,
            rotationY: 0,
            color: "#ffffff",
            duration: 1.3,
            scrollTrigger: {
              trigger: this.$refs.contentBox2,
              start: "top 85%",
              end: "top 60%",
              toggleActions: "play none none reverse",
            }
          }
        );

        // 文字动画（淡入+轻微浮动）
        gsap.fromTo(
          this.$refs.text2,
          {
            opacity: 0,
            y: 50,
            filter: "blur(8px)"
          },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1.5,
            delay: 0.2,
            scrollTrigger: {
              trigger: this.$refs.contentBox2,
              start: "top 80%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            }
          }
        );

        // 地图图片动画（3D效果）
        if (this.$refs.logoImg2) {
          const map = this.$refs.logoImg2.querySelector('img');
          gsap.fromTo(
            map,
            {
              opacity: 0,
              scale: 0.5,
              rotationX: 60,
              rotationY: 30,
              z: -500
            },
            {
              opacity: 1,
              scale: 1,
              rotationX: 0,
              rotationY: 0,
              z: 0,
              duration: 2,
              ease: "power4.out",
              scrollTrigger: {
                trigger: this.$refs.logoImg2,
                start: "top 95%",
                end: "top 60%",
                toggleActions: "play none none reverse",
              }
            }
          );

          // 添加悬停效果
          this.$refs.logoImg2.addEventListener('mouseenter', () => {
            gsap.to(map, {
              scale: 1.05,
              duration: 0.8,
              ease: "power2.out"
            });
          });

          this.$refs.logoImg2.addEventListener('mouseleave', () => {
            gsap.to(map, {
              scale: 1,
              duration: 0.8,
              ease: "power2.out"
            });
          });
        }

        // 背景浮动动画
        gsap.to(this.$refs.contentBox2, {
          backgroundPosition: "0% 50%",
          duration: 20,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }

      // 添加页面进入时的整体效果
      gsap.fromTo('.partner-container',
        { opacity: 0, filter: 'blur(5px)' },
        { opacity: 1, filter: 'blur(0px)', duration: 1.5, ease: 'power2.out' }
      );
    },

    createParticles() {
      const particlesContainer = this.$refs.particles;
      if (!particlesContainer) return;

      // 创建粒子
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        
        // 随机属性
        const size = Math.random() * 8 + 2;
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        const delay = Math.random() * 5;
        const duration = Math.random() * 10 + 10;
        
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.left = `${posX}%`;
        particle.style.top = `${posY}%`;
        particle.style.opacity = Math.random() * 0.3 + 0.1;
        
        particlesContainer.appendChild(particle);

        // 粒子浮动动画
        gsap.to(particle, {
          x: `+=${Math.random() * 100 - 50}`,
          y: `+=${Math.random() * 100 - 50}`,
          rotation: Math.random() * 360,
          duration: duration,
          delay: delay,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }
    }
  },
  beforeUnmount() {
    // 清理所有ScrollTrigger实例
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    
    if (this.$refs.logoImg2) {
      this.$refs.logoImg2.removeEventListener('mouseenter', () => {});
      this.$refs.logoImg2.removeEventListener('mouseleave', () => {});
    }
  }
};
</script>

<style scoped>
.partner-container {
  width: 100%;
  position: relative;
  overflow: hidden;
}


.banner {
  position: relative;
  width: 100%;
  height: 150vh; /* 默认高度 */
  background: url("@/assets/image/about/partner_banner.png") center/cover;
  display: flex;
  flex-direction: column;
  color: white;
  text-align: left;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}

.banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(0,0,0,0.4), transparent 30%);
  z-index: 0;
}

.banner-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  width: 100%;
  margin-left: 4%;
  margin-top: 14rem;
  perspective: 1000px;
}

.banner-title {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 500;
  display: inline-block;
  transform-style: preserve-3d;
}

.banner-title .char {
  will-change: transform, opacity;
}

.banner-subtitle {
  font-size: 1.8rem;
}


.breadcrumb {
  position: absolute;
  top: 31rem;
  left: 5%;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0;
  font-size: 1.15rem;
  color: #999999;
  white-space: nowrap;
  will-change: transform, opacity;
  transform-style: preserve-3d;
}

.breadcrumb-item {
  opacity: 0.8;
}
.breadcrumb-item:hover{
      opacity:1;
}
.breadcrumb-item.current {
  opacity: 1;
  text-decoration: underline;
  color: #B2B2B2;
}


.breadcrumb-separator {
  width: 16px;
  height: 16px;
  margin: 0 4px;
  transform: translateZ(20px);
}

.partner-description {
  position: absolute;
  top: 42rem;
  left: 5%; 
  z-index: 10;
  max-width: 640px;
  text-align: left;
  will-change: transform, opacity, filter;
}

.partner-description p {
  font-size: 1.3rem;
  line-height: 2;
  opacity: 0.9;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.content-box {
  width: 100%;
  padding: 5rem 0;
  position: relative;
  overflow: hidden;
  right: 0;
}

.content-box .main {
  width: 80%;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  perspective: 1000px;
}

.content-box h3 {
  font-size: 2.4rem;
  font-weight: 600;
  margin-bottom: 10px;
  will-change: transform, opacity;
  transform-style: preserve-3d;
}

.content-box p {
  font-size: 1.4rem;
  will-change: transform, opacity, filter;
}

.logoimg {
  width: 80%;
  margin: 10rem auto 3rem auto;
  transition: transform 0.3s ease;
  transform-style: preserve-3d;
}

.logoimg img {
  width: 100%;
  height: auto;
  display: block;
  will-change: transform, opacity;
  transform-style: preserve-3d;
}

.mapwrap {
  background: url("@/assets/image/about/partner_3bj.png") center/cover;
  display: flex;
  color: #fff;
  position: relative;
  background-attachment: fixed;
}

.mapwrap::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 100, 200, 0.2), rgba(0, 0, 0, 0.2));
}

.mapwrap .main {
  position: relative;
  z-index: 2;
}

.mapwrap .logoimg {
  width: 100%;
  will-change: transform;
}

/* 粒子效果 */
.particles {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  background: linear-gradient(45deg, #4fc3f7, #29b6f6);
  border-radius: 50%;
  filter: blur(2px);
  box-shadow: 0 0 20px rgba(79, 195, 247, 0.5);
  will-change: transform, opacity;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .banner {
    height: 70vh; /* 默认移动端高度 */
  }

  /* 中文模式下的移动端高度 */
  .banner[data-locale="zh"] {
    height: 50vh;
  }

  /* 英文模式下的移动端高度 */
  .banner[data-locale="en"] {
    height: 70vh;
  }

  .banner-title {
    font-size: 2rem;
  }

  .banner-content {
    margin-left: 0;
    margin-top: 7rem;
  }

  .breadcrumb {
    top: 17rem;
    left: 3%;
    font-size: 1.2rem;
  }

  .breadcrumb-separator {
    width: 12px;
    height: 12px;
  }

  .partner-description {
    top: 21rem;
    left: 3%;
    max-width: 90%;
  }

  .partner-description p {
    font-size: 1.2rem;
  }
  
  .main {
    width: 90%;
  }
  
  .logoimg {
    width: 100%;
    margin: 6rem auto 3rem auto;
  }
  
  .content-box h3 {
    font-size: 1.8rem;
  }

  .mapwrap {
    background-attachment: scroll;
  }
}

@media screen and (min-width: 1920px) {
  .banner {
    height: 135vh;
  }
  .breadcrumb {
    top: 43rem;
  }
  
  .partner-description {
    top: 57rem;
  }
  .banner-content{
    margin-top: 20rem;
  }
}
</style>